import {
  Wallet,
  TrendingUp,
  Boxes,
  Package,
  ClipboardCheck,
  AlertTriangle,
} from 'lucide-react';
import { KPI } from '@/components/ui/KPI';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';

export function DashboardPage() {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">
          Panel principal · Ejercicio 2026
        </p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">
          Resumen ejecutivo
        </h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Visión consolidada del ente público. Datos en vivo desde todos los módulos activos.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI
          label="Presupuesto ejercido"
          value="$ 142.8M"
          subValue="/ $ 480.0M"
          delta={{ value: '29.7%', direction: 'up' }}
          icon={<Wallet size={16} />}
          accent="brand"
        />
        <KPI
          label="Ingresos recaudados"
          value="$ 98.4M"
          delta={{ value: '12.3% YoY', direction: 'up' }}
          icon={<TrendingUp size={16} />}
          accent="success"
        />
        <KPI
          label="Bienes activos"
          value="3,412"
          subValue="valor $ 284M"
          delta={{ value: '+48 altas', direction: 'up' }}
          icon={<Boxes size={16} />}
          accent="neutral"
        />
        <KPI
          label="Alertas abiertas"
          value="7"
          subValue="3 críticas"
          delta={{ value: '−2 hoy', direction: 'down' }}
          icon={<AlertTriangle size={16} />}
          accent="warn"
        />
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Actividad reciente"
            subtitle="Operaciones registradas en las últimas 24 h"
            action={
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                En vivo
              </span>
            }
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-border">
              {sampleActivity.map((a) => (
                <li key={a.id} className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-muted/50">
                  <span className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full ${a.iconBg}`}>
                    {a.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium text-ink">{a.title}</p>
                    <p className="truncate text-[12px] text-ink-mute">{a.detail}</p>
                  </div>
                  <span className="num text-[12px] text-ink-soft">{a.when}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Próximos vencimientos" subtitle="Garantías, contratos y mantenimientos" />
          <CardBody>
            <ul className="flex flex-col gap-3">
              {sampleExpirations.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium text-ink">{e.title}</p>
                    <p className="truncate text-[12px] text-ink-mute">{e.detail}</p>
                  </div>
                  <span
                    className={`num rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      e.critical
                        ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                        : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    }`}
                  >
                    {e.days}d
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </section>

      <section className="rounded-lg border border-dashed border-border bg-surface/60 p-8 text-center">
        <p className="text-[13px] font-medium text-ink-mute">
          Los datos mostrados son de ejemplo. En cuanto conectemos los módulos de Proveedores,
          Almacén y Bienes, este panel empezará a poblarse con métricas reales.
        </p>
      </section>
    </div>
  );
}

const sampleActivity = [
  {
    id: 1,
    title: 'Alta de bien patrimonial · Impresora HP LaserJet',
    detail: 'Responsable: María G. Ruiz — Unidad: Tesorería',
    when: '14:32',
    icon: <Package size={14} className="text-emerald-700" />,
    iconBg: 'bg-emerald-50 dark:bg-emerald-900/40',
  },
  {
    id: 2,
    title: 'Orden de compra OC-2026-0421 autorizada',
    detail: 'Proveedor: Papelería del Valle, S.A. — Monto $ 48,320.00',
    when: '13:58',
    icon: <ClipboardCheck size={14} className="text-brand-700" />,
    iconBg: 'bg-brand-50 dark:bg-brand-900/40',
  },
  {
    id: 3,
    title: 'Conciliación bancaria BANORTE cerrada',
    detail: 'Diferencia 0.00 · periodo marzo 2026',
    when: '11:22',
    icon: <Wallet size={14} className="text-ink" />,
    iconBg: 'bg-stone-100 dark:bg-white/5',
  },
  {
    id: 4,
    title: 'Alerta: inventario mínimo en Almacén Central',
    detail: 'Artículos por debajo del mínimo: 5',
    when: '09:47',
    icon: <AlertTriangle size={14} className="text-amber-700" />,
    iconBg: 'bg-amber-50 dark:bg-amber-900/40',
  },
];

const sampleExpirations = [
  { id: 1, title: 'Póliza de seguro vehicular', detail: 'Flotilla municipal', days: 4,  critical: true  },
  { id: 2, title: 'Contrato de limpieza',        detail: 'Edificio principal',  days: 12, critical: false },
  { id: 3, title: 'Mantenimiento UPS datacenter',detail: 'Sala servidores',     days: 23, critical: false },
  { id: 4, title: 'Garantía equipo cómputo',     detail: 'Lote 2024 · 40 pzas', days: 45, critical: false },
];
